import { Injectable, NgZone, OnDestroy, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { AuthActions, authFeature } from '../../features/auth/state';
import {
  AUTH_LOGOUT_EVENT_KEY,
  AUTH_REFRESH_TOKEN_KEY,
  AUTH_TOKEN_KEY,
} from '../constants/auth-storage.constants';
import { readStoredSession } from '../utils/auth-session-storage';
import { RELOAD_APP } from './app-reload';

// Keeps this tab in step with the others through the `storage` event, which fires only in the
// tabs that did not write. Nothing here writes to storage, so tabs cannot echo each other.
@Injectable({ providedIn: 'root' })
export class AuthSessionSyncService implements OnDestroy {
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly ngZone = inject(NgZone);
  private readonly reloadApp = inject(RELOAD_APP);
  private readonly token = this.store.selectSignal(authFeature.selectToken);
  private readonly refreshToken = this.store.selectSignal(authFeature.selectRefreshToken);
  private readonly publicId = this.store.selectSignal(authFeature.selectPublicId);

  start(): void {
    window.addEventListener('storage', this.onStorageChange);
  }

  ngOnDestroy(): void {
    window.removeEventListener('storage', this.onStorageChange);
  }

  private readonly onStorageChange = (event: StorageEvent): void => {
    if (event.storageArea !== localStorage) {
      return;
    }

    if (event.key === AUTH_LOGOUT_EVENT_KEY && event.newValue) {
      this.ngZone.run(() => this.onSessionClosedElsewhere());
    } else if (event.key === AUTH_TOKEN_KEY || event.key === AUTH_REFRESH_TOKEN_KEY) {
      this.ngZone.run(() => this.followStoredTokens());
    }
  };

  private onSessionClosedElsewhere(): void {
    if (this.token()) {
      this.store.dispatch(AuthActions.sessionEndedElsewhere());
    }

    this.router.navigate(['/login']);
  }

  // Another tab renewed the pair, or removed it. A tab without a session has nothing to follow.
  private followStoredTokens(): void {
    if (!this.token()) {
      return;
    }

    const stored = readStoredSession();

    if (!stored.token) {
      this.onSessionClosedElsewhere();
      return;
    }

    // A different user signed in elsewhere: none of this tab's state may outlive them.
    if (stored.publicId && this.publicId() && stored.publicId !== this.publicId()) {
      this.reloadApp();
      return;
    }

    if (stored.token !== this.token() || stored.refreshToken !== this.refreshToken()) {
      this.store.dispatch(
        AuthActions.tokensRenewed({ token: stored.token, refreshToken: stored.refreshToken }),
      );
    }
  }
}
